import { createContext, use } from "react";
import { useQuery } from "@tanstack/react-query";

import { useProfile } from "@workspace/profile";
import { settingsApi } from "@workspace/settings/api";

import { useFormatCurrency } from "../../components/use-format-currency";
import type { Money } from "../../types";
import type { Currency, CurrencyDisplay, DisplayRate } from "../types";
import { Currencies } from "../types";

/** HA-928: the display read, with each currency completed from the static list (names, symbols, decimals). */
type DisplayCurrencies = Omit<CurrencyDisplay, "currencies"> & {
	currencies: Currency[];
	primaryCurrency: Currency;
	activeCurrencies: Currency[];
};

export function useAllCurrencies({
	value = 0,
	currency = "USD",
	exchangeRates = [],
	primaryCurrency: currentCurrency,
	currencies = [],
}: {
	value?: number | null | undefined;
	currency?: string | undefined | null;
	exchangeRates?: DisplayRate[];
	primaryCurrency: Currency;
	currencies?: Currency[];
}) {
	const activeRates = exchangeRates.filter(
		(x) =>
			x.fromCurrency === (currency || currentCurrency.code) &&
			currencies.find((c) => c.code === x.toCurrency)?.active,
	);

	const currencyRates = activeRates.map((x) => {
		const current = currencies.find((c) => c.code === x.toCurrency);
		return {
			code: current?.code,
			toCurrency: x.toCurrency,
			convertedValue: x.rate * (value || 0),
		};
	});

	return { currencyRates };
}

export function useAppCurrencies() {
	const { tenant } = useProfile();

	const currencyQuery = useQuery({
		queryKey: [tenant, "currencies", "data"],
		queryFn: () => settingsApi.get<CurrencyDisplay>(tenant, "currencies"),
		select: (data): DisplayCurrencies => {
			const currencies = data.currencies.map((currency) => ({
				...Currencies[currency.code],
				...currency,
			})) as Currency[];

			const primaryCurrency: Currency = (currencies.find(
				(currency) => currency.isPrimary && currency.active,
			) || Currencies.USD) as Currency;

			return {
				primaryCurrency,
				activeCurrencies: [
					primaryCurrency,
					...currencies.filter(
						(currency) =>
							currency.active &&
							data.exchangeRates.some(
								(e) =>
									e.toCurrency === currency.code &&
									e.fromCurrency === primaryCurrency.code,
							),
					),
				],
				...data,
				currencies,
			};
		},
	});

	function exchange(amount: Money | undefined, code: string) {
		if (!amount || !amount.value || !amount.currency) {
			return amount;
		}

		const exchangeRate = currencyQuery.data?.exchangeRates.find(
			(rate) =>
				rate.fromCurrency === amount.currency && rate.toCurrency === code,
		);

		if (exchangeRate) {
			return {
				...amount,
				value: amount.value * exchangeRate.rate,
				currency: code,
			};
		}

		return amount;
	}

	const primaryCurrency = (currencyQuery.data?.primaryCurrency ||
		Currencies.USD) as Currency;

	return {
		formatter: useFormatCurrency(primaryCurrency.code),
		exchange,
		useExchangedValues: (
			value: number | null | undefined,
			currency: string = primaryCurrency.code,
		) => {
			const curr =
				currencyQuery.data?.activeCurrencies.find((c) => c.code === currency) ||
				primaryCurrency;

			return {
				hasRates: (currencyQuery.data?.activeCurrencies.length || 0) > 1,
				...useAllCurrencies({
					value,
					currency,
					primaryCurrency: curr,
					currencies: currencyQuery.data?.activeCurrencies || [],
					exchangeRates: currencyQuery.data?.exchangeRates || [],
				}),
			};
		},

		primaryCurrency,
		exchangeRates: [] as DisplayRate[],
		currencies: [] as Currency[],
		activeCurrencies: [] as Currency[],
		...(currencyQuery.data || {}),

		money: (value: number, currency?: string) =>
			({
				value,
				currency: currency || primaryCurrency.code,
				version: 2,
			}) as Money,
	};
}

export function useCurrency({
	venueId,
	tenant,
}: {
	venueId: string;
	tenant: string;
}) {
	const currencyQuery = useQuery({
		queryKey: [tenant, venueId, "currencies"],
		queryFn: () =>
			settingsApi.get<CurrencyDisplay>(tenant, "currencies", venueId),
		select: (data): DisplayCurrencies => {
			const currencies = data.currencies.map((currency) => ({
				...Currencies[currency.code],
				...currency,
			})) as Currency[];

			const primaryCurrency: Currency = (currencies.find(
				(currency) => currency.isPrimary && currency.active,
			) || Currencies.USD) as Currency;

			return {
				primaryCurrency,
				activeCurrencies: [
					primaryCurrency,
					...currencies.filter(
						(currency) =>
							currency.active &&
							data.exchangeRates.some(
								(e) =>
									e.toCurrency === currency.code &&
									e.fromCurrency === primaryCurrency.code,
							),
					),
				],
				...data,
				currencies,
			};
		},
	});

	function exchange(amount: Money | undefined, code: string) {
		if (!amount || !amount.value || !amount.currency) {
			return amount;
		}

		const exchangeRate = currencyQuery.data?.exchangeRates.find(
			(rate) =>
				rate.fromCurrency === amount.currency && rate.toCurrency === code,
		);

		if (exchangeRate) {
			return {
				value: amount.value * exchangeRate.rate,
				currency: code,
			};
		}

		return amount;
	}

	const primaryCurrency = (currencyQuery.data?.primaryCurrency ||
		Currencies.USD) as Currency;

	return {
		formatter: useFormatCurrency(primaryCurrency.code),
		exchange,
		useExchangedValues: (value: number | null | undefined) => ({
			hasRates: (currencyQuery.data?.activeCurrencies.length || 0) > 1,
			...useAllCurrencies({
				value,
				currency: primaryCurrency.code,
				primaryCurrency,
				currencies: currencyQuery.data?.activeCurrencies || [],
				exchangeRates: currencyQuery.data?.exchangeRates || [],
			}),
		}),

		primaryCurrency,
		exchangeRates: [] as DisplayRate[],
		currencies: [] as Currency[],
		activeCurrencies: [] as Currency[],
		...(currencyQuery.data || {}),

		money: (value: number, currency?: string) =>
			({
				value,
				currency: currency || primaryCurrency.code,
				version: 2,
			}) as Money,
	};
}

export const CurrencyContext = createContext<ReturnType<
	typeof useCurrency
> | null>(null);

export const useCurrencyContext = () => {
	const context = use(CurrencyContext);
	if (!context) {
		throw new Error(
			"useCurrencyContext must be used within a CurrencyContextProvider",
		);
	}

	return context;
};
